/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../lib/@waca/dashboard-common/dist/ui/interaction/Utils',
	'../../../../app/nls/StringResources',
	'underscore',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], function(Util, stringResources, _, APIFactory, ContentActionsProviderAPI) {

	class OrderAction {

		constructor({ features }) {
			if(features) {
				this.dashboard = features.API;
				features.ContentActions.registerProvider('order', this.getAPI());
				this._dashboardState = features.DashboardState;
				this._icons = features.Icons;
			}
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [ContentActionsProviderAPI]);
			}
			return this._api;
		}

		getLifeCycleHandlers() {
			return [{
				name: 'post:dashboard.initialize',
				action: this.postDashboardInitialize.bind(this)
			}];
		}

		postDashboardInitialize() {
			if(this.dashboard) {
				this.controller = this.dashboard.getFeature('InteractionController.internal');
			}
			return Promise.resolve();
		}

		destroy() {
			this.dashboard = null;
			this.controller = null;
		}

		getNodes(idList) {
			return Util.getNodes(this.controller, idList);
		}

		getParentModel(idList) {
			const nodes = this.getNodes(idList);
			if (nodes.length > 0) {
				return nodes[0]._layout.model.getParent();
			}
		}

		/**
		 * Get the list of items that can be ordered and that belong to the same parent
		 */
		getItems(idList){
			const parentModel = this.getParentModel(idList);
			return parentModel && parentModel.items.filter(function(item){
				return item.type !== 'templateDropZone' && item.type !== 'templateIndicator';
			});
		}

		_isAvailable(idList) {
			const nodes = this.getNodes(idList);
			let items = this.getItems(idList);
			return 	nodes.length &&
					items.length > 1 &&
					this.dashboard.getMode() === this.dashboard.MODES.EDIT &&
					_.find(nodes, node => items.indexOf(node._layout.model) === -1) === undefined;
		}

		_isDisabled() {
			return this._dashboardState.getUiState().focus;
		}

		getContentActionList(idList) {
			if (this._isAvailable(idList)) {
				return [{
					name: 'orderToFront',
					label: stringResources.get('toolbarActionOrderToFront'),
					icon: this._icons.getIcon('bring-to-front').id,
					type: 'Button',
					disabled: this._isDisabled.bind(this),
					actions: {
						apply: this.orderContent.bind(this, idList, this.getItems(idList).length - this.getNodes(idList).length)
					}
				}, {
					name: 'orderToBack',
					label: stringResources.get('toolbarActionOrderToBack'),
					icon: this._icons.getIcon('send-to-back').id,
					type: 'Button',
					disabled: this._isDisabled.bind(this),
					actions: {
						apply: this.orderContent.bind(this, idList, 0)
					}
				}];
			}
			return [];
		}

		/**
		 *  Set the order of the selected nodes.
		 */
		orderContent(idList, order){
			const items = this.getItems(idList);
			const nodes = this.getNodes(idList);
			const selectedModels = _.map(nodes, node => node._layout.model);
			const parentModel = this.getParentModel(idList);
			const updateArray = [];
			const sortedSelection = [];
			const sortedLeftOver = [];
			let model, i, j, newOrder;
			for (i = 0, j = 0, newOrder = false; i < items.length; i++){
				model = items[i];
				if(selectedModels.indexOf(model) === -1){
					sortedLeftOver.push(model);
					j++;
				}else{
					sortedSelection.push(model);
					if(j!==order) {
						newOrder = true;
					}
				}
			}
			if (newOrder) {
				//Only update the model if this will result in a net change
				const modelToInsertBefore = sortedLeftOver[order];
				for (i = 0; i < selectedModels.length; i++){
					model = selectedModels[i];
					updateArray.push({
						id: model.id,
						parentId: parentModel.id,
						insertBefore:modelToInsertBefore ? modelToInsertBefore.id: null
					});
				}

				parentModel.updateModel({ updateArray: updateArray });
			}
		}
	}

	return OrderAction;

});
